import { CertificateService } from '@/@types/generated'
import { Button } from '@/components/ui/button'
import { Header } from '@/components/ui/header'
import { buildPath } from '@/routes'
import { XCircleIcon } from 'lucide-react'
import { PropsWithChildren, useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { useCompany } from '../../hooks/company-provider'
import Loading from '@/components/Atoms/Loading'
import { useError } from '@/hooks/useError'
import { useRenewal } from '../../hooks/renewal-provider'
import { RenewalHazmatChoice } from './RenewalHazmatChoice'

export function RenewalRequiredInsuranceGuard({ children }: PropsWithChildren) {
  const { showError } = useError()
  const navigate = useNavigate()
  const { company } = useCompany()
  const { hazmatChoice } = useRenewal()
  const [hasRequiredInsurance, setHasRequiredInsurance] = useState<
    boolean | null
  >(null)

  const certificateNumber = company.motorCarrier?.certificate?.certificateNumber

  // CRD-009: check against the Hazmat status chosen for the renewal. A new choice keeps the
  // previous answer on screen while it loads, so the step in progress stays mounted.
  useEffect(() => {
    if (!certificateNumber) return

    let isCurrent = true
    CertificateService.getCertificateRenewalInsurance(
      certificateNumber,
      hazmatChoice ?? undefined
    )
      .then(({ hasRequiredInsurance }) => {
        if (isCurrent) setHasRequiredInsurance(hasRequiredInsurance)
      })
      .catch((err) => {
        if (!isCurrent) return
        showError(err, 'Failed to verify insurance requirements')
        navigate(
          buildPath('/companies/:businessEntityId', {
            businessEntityId: company.id,
          })
        )
      })
    return () => {
      isCurrent = false
    }
  }, [certificateNumber, hazmatChoice, company.id, navigate, showError])

  // Loading
  if (hasRequiredInsurance === null)
    return (
      <Loading
        variant="fullscreen"
        message="Verifying insurance requirements..."
      />
    )

  // Ensure carrier has required insurance before renewing
  if (hasRequiredInsurance === false)
    return (
      <div className="mt-6 grid justify-center gap-4 text-center">
        <XCircleIcon
          className="stroke-destructive h-24 w-24 justify-self-center"
          aria-hidden="true"
        />
        <Header variant="h3">Missing Required Insurance</Header>
        <p>
          This certificate is missing one or more required insurance policies.
        </p>
        <p>
          Please contact your insurance provider to update your certificate
          insurance before proceeding.
        </p>
        <RenewalHazmatChoice className="text-left" />

        <div className="flex justify-center gap-4">
          <Button asChild>
            <Link
              to={buildPath('/companies/:businessEntityId/insurance', {
                businessEntityId: company.id,
              })}
            >
              View Insurance Policies
            </Link>
          </Button>
        </div>
      </div>
    )

  return children
}
